import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSubjects, getChapters, getCards, createCard, updateCard, deleteCard } from '../../api';

const emptyC = { chapter_id: '', question_text: '', answer_text: '' };

export default function AdminCards() {
  const qc = useQueryClient();
  const [selSubject, setSelSubject] = useState('');
  const [selChapter, setSelChapter] = useState('');
  const [form, setForm] = useState(emptyC);
  const [formSubject, setFormSubject] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const subjects = useQuery({ queryKey: ['subjects'], queryFn: () => getSubjects().then(r => r.data) });
  const chapters = useQuery({ queryKey: ['chapters', selSubject], queryFn: () => getChapters(selSubject).then(r => r.data), enabled: !!selSubject });
  const formChapters = useQuery({ queryKey: ['chapters', formSubject], queryFn: () => getChapters(formSubject).then(r => r.data), enabled: !!formSubject });
  const cards = useQuery({ queryKey: ['cards', selChapter], queryFn: () => getCards(selChapter || undefined).then(r => r.data), enabled: !!selChapter });

  const createC = useMutation({ mutationFn: createCard, onSuccess: () => { qc.invalidateQueries(['cards']); setForm(emptyC); setShowForm(false); setEditing(null); } });
  const updateC = useMutation({ mutationFn: ({ id, data }) => updateCard(id, data), onSuccess: () => { qc.invalidateQueries(['cards']); setEditing(null); setShowForm(false); } });
  const deleteC = useMutation({ mutationFn: deleteCard, onSuccess: () => qc.invalidateQueries(['cards']) });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...form, chapter_id: parseInt(form.chapter_id) };
    if (editing) updateC.mutate({ id: editing, data: { question_text: payload.question_text, answer_text: payload.answer_text } });
    else createC.mutate(payload);
  };

  const startEdit = (c) => {
    setEditing(c.id); setShowForm(true);
    setForm({ chapter_id: c.chapter_id, question_text: c.question_text, answer_text: c.answer_text });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div><h1 style={{ fontSize: 26 }}>Flashcards</h1><p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Create cards for student practice</p></div>
        <button className="btn btn-primary" onClick={() => { setShowForm(true); setEditing(null); setForm(emptyC); }}>+ Add Card</button>
      </div>

      <div className="card" style={{ marginBottom: 20, padding: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <select className="form-select" style={{ width: 180 }} value={selSubject} onChange={e => { setSelSubject(e.target.value); setSelChapter(''); }}>
            <option value="">Select Subject</option>
            {subjects.data?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {selSubject && (
            <select className="form-select" style={{ width: 200 }} value={selChapter} onChange={e => setSelChapter(e.target.value)}>
              <option value="">Select Chapter</option>
              {chapters.data?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 20 }}>{editing ? 'Edit Card' : 'Add New Flashcard'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <select className="form-select" value={formSubject} onChange={e => { setFormSubject(e.target.value); setForm(f => ({...f, chapter_id: ''})); }}>
                  <option value="">Select</option>
                  {subjects.data?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Chapter *</label>
                <select className="form-select" value={form.chapter_id} disabled={!formSubject}
                  onChange={e => setForm(f => ({...f, chapter_id: e.target.value}))}>
                  <option value="">Select</option>
                  {formChapters.data?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Question *</label>
              <textarea className="form-textarea" value={form.question_text} required
                placeholder="e.g. Who gave the five kingdom classification?"
                onChange={e => setForm(f => ({...f, question_text: e.target.value}))} />
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Answer *</label>
              <textarea className="form-textarea" value={form.answer_text} required
                onChange={e => setForm(f => ({...f, answer_text: e.target.value}))} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" type="submit">{editing ? 'Update' : 'Add Card'}</button>
              <button className="btn btn-ghost" type="button" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {selChapter && (
        <div>
          {cards.isLoading ? <div className="spinner"></div> : (
            <div style={{ display: 'grid', gap: 14 }}>
              {cards.data?.length === 0 ? <div className="empty-state card">No cards in this chapter yet.</div>
                : cards.data?.map(c => (
                <div key={c.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>Q: {c.question_text}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 14, borderLeft: '3px solid var(--success)', paddingLeft: 12 }}>
                        A: {c.answer_text}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => startEdit(c)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => window.confirm('Delete card?') && deleteC.mutate(c.id)}>Del</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
